import React, { useState, useEffect, useMemo } from "react";
import { Input, Tag, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

interface TemplateVariableInputProps {
  template: any;
  onChange: (variables: string[]) => void;
  value: string[];
  previousContextFields?: any[];
}

const TemplateVariableInput: React.FC<TemplateVariableInputProps> = ({
  template,
  onChange,
  value = [],
  previousContextFields = [],
}) => {
  const [variables, setVariables] = useState<string[]>(value);

  useEffect(() => {
    setVariables(value);
  }, [value]);

  // Extract variable count from template
  const variableCount = useMemo(() => {
    if (!template?.structure?.variables) return 0;
    return Object.keys(template.structure.variables).length;
  }, [template]);

  // Get example values from template
  const exampleValues = useMemo(() => {
    const bodyComponent = template?.structure?.components?.find(
      (comp: any) => comp.type === "BODY"
    );

    return bodyComponent?.example?.body_text?.[0] || [];
  }, [template]);

  const handleVariableChange = (index: number, value: string) => {
    const newVariables = [...variables];
    newVariables[index] = value;
    setVariables(newVariables);
    onChange(newVariables);
  };

  if (variableCount === 0) return null;

  return (
    <div className="template-variables-input">
      <div className="variables-header">
        <span className="variables-title">Template Variables</span>
        <Tooltip title="Enter values in order. Use {{ }} syntax for dynamic values from previous nodes.">
          <InfoCircleOutlined style={{ color: "#888", marginLeft: "8px" }} />
        </Tooltip>
      </div>

      <div className="variables-inputs">
        {Array.from({ length: variableCount }).map((_, index) => (
          <div key={index} className="variable-input-group">
            <div className="variable-label">
              <Tag color="blue">{`{{${index + 1}}}`}</Tag>
              {exampleValues[index] && (
                <span className="variable-example">
                  Example: <i>{exampleValues[index]}</i>
                </span>
              )}
            </div>
            <Input
              placeholder={`Enter value for {{${
                index + 1
              }}} or use {{variable}}`}
              value={variables[index] || ""}
              onChange={(e) => handleVariableChange(index, e.target.value)}
              allowClear
            />
          </div>
        ))}
      </div>

      {/* Context variables hint */}
      {previousContextFields.length > 0 && (
        <div className="context-hint">
          💡 Available variables:{" "}
          {previousContextFields
            .slice(0, 3)
            .map((f) => `{{${f.key}}}`)
            .join(", ")}
          {previousContextFields.length > 3 &&
            ` and ${previousContextFields.length - 3} more`}
        </div>
      )}
    </div>
  );
};

export default TemplateVariableInput;
